"use client";

import Link from "next/link";
import {
  storyblokEditable,
  StoryblokComponent,
  renderRichText,
} from "@storyblok/react/rsc";
import { GridContainer } from "@/components/layout/GridContainer";
import { JobBadge } from "@/components/layout/JobBadge";
import { ListBlock } from "@/components/layout/ListBlock";
import { Button } from "@/components/ui/button";
import { RichText } from "@/components/typography/RichText";
import { getUrlFromLinkObject } from "@/lib/storyblok/utils";
import type { JobStoryblok } from "@/component-types-sb";

type JobProps = {
  blok: JobStoryblok;
};

const Job = ({ blok }: JobProps) => {
  const buttonHref = getUrlFromLinkObject(blok.button_link);

  return (
    <div {...storyblokEditable(blok)}>
      <GridContainer className="py-8 md:py-12">
        <div className="col-span-12 md:col-span-8 md:col-start-3 lg:col-span-8 lg:col-start-3 flex flex-col gap-6">
          {/* Header: CTA Button top-right */}
          {buttonHref && blok.button_label && (
            <div className="flex justify-end">
              <Button variant="sun" asChild>
                <Link href={buttonHref}>{blok.button_label}</Link>
              </Button>
            </div>
          )}

          {/* Title */}
          <h1 className="text-headline-xl text-black">{blok.title}</h1>

          {/* Badges */}
          {blok.badges && blok.badges.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {blok.badges.map((badge) => (
                <JobBadge
                  key={badge._uid}
                  label={badge.label}
                  color={badge.color}
                />
              ))}
            </div>
          )}

          {/* Description — same prose styling as blog articles */}
          {blok.description && (
            <RichText html={renderRichText(blok.description)} />
          )}

          {/* List items — same pattern as info_hero */}
          {blok.info_list_items && blok.info_list_items.length > 0 && (
            <ListBlock
              variant="divider"
              className="md:max-w-xl text-black-95 [&>div>*:first-child]:border-t [&>div>*:first-child]:border-[rgba(0,0,0,0.12)] [&>div>*:first-child]:pt-2"
            >
              {blok.info_list_items.map((nestedBlok: any) => (
                <StoryblokComponent blok={nestedBlok} key={nestedBlok._uid} />
              ))}
            </ListBlock>
          )}
        </div>
      </GridContainer>

      {/* Body content */}
      {blok.body && blok.body.length > 0 && (
        <section className="flex flex-col">
          {blok.body.map((nestedBlok) => (
            <StoryblokComponent key={nestedBlok._uid} blok={nestedBlok} />
          ))}
        </section>
      )}
    </div>
  );
};

export default Job;
